<script setup lang="ts">
// Imports
import Drawer from 'primevue/drawer'
import Button from 'primevue/button'

// Types
interface MenuItem {
  href: string
  name: string
  icon: string
  action?: string
}

interface Props {
  visible: boolean
  navigation: MenuItem[]
}

// Props and Emits
defineProps<Props>()
const emit = defineEmits<{
  'update:visible': [value: boolean]
  'close': []
}>()

// Composables
const { t } = useI18n()
const sbAccessToken = useCookie('sb-access-token')
const businessId = useRouteParam('buuid')

// Refs
const business = ref()
const render = ref<boolean>(false)
const isClearingCache = ref(false)

// Stores
const commerceStore = useCommerceStore()

// Methods
function handleItemClick(item: MenuItem) {
  emit('close')

  if (item.action === 'logout') {
    navigateTo('/auth/logout')
  }
}

async function handleClearCache() {
  isClearingCache.value = true
  try {
    // Clear all cached stores
    const paymentMethodsStore = usePaymentMethodsStore()
    const discountsStore = useDiscountsStore()

    await Promise.all([
      commerceStore.clearCache(),
      paymentMethodsStore.fetchItems(true),
      discountsStore.fetchDiscounts(true),
    ])

    useSuccessToast('Cache cleared successfully')
    emit('close')
  }
  catch (error) {
    console.error('Failed to clear cache:', error)
    useErrorToast('Failed to clear cache')
  }
  finally {
    isClearingCache.value = false
  }
}

// Side effects
watch(
  [() => businessId, () => sbAccessToken.value],
  async ([newBuuid, _newSbAccessToken]) => {
    if (typeof newBuuid === 'string') {
      try {
        business.value = await $fetch(`/api/business/${newBuuid}`, {
          method: 'GET',
        })
        render.value = true
      }
      catch (error) {
        console.error('Failed to fetch business:', error)
      }
    }
  },
  {
    immediate: true,
  },
)
</script>

<template>
  <Drawer
    :visible="visible"
    header=""
    :pt="{
      header: { class: 'py-2 px-3' },
      content: { class: 'p-0' },
    }"
    @update:visible="$emit('update:visible', $event)"
  >
    <div class="flex h-full flex-col">
      <div
        v-show="render"
        class="px-5 pt-2"
      >
        <NuxtLink
          to="/store/pos"
          class=""
        >
          <img
            v-if="business?.business_photo?.variants"
            :src="getImageBySize(business.business_photo.variants, 'ratioSmall')"
            :alt="`Logo of ${business.name}`"
            class="mr-2 h-16 rounded-md lg:mr-2 print:mx-auto print:h-16"
          >
        </NuxtLink>
      </div>
      <div class="h-15 flex items-center justify-center" />
      <div>
        <ul class="m-0 list-none p-0 px-2">
          <!-- Regular navigation items (non-logout) -->
          <li
            v-for="item in navigation.filter(item => item.action !== 'logout')"
            :key="item.name"
          >
            <NuxtLink
              :to="item.href"
              class="flex items-center rounded border p-3 no-underline transition duration-150 hover:bg-gray-100"
              @click="handleItemClick(item)"
            >
              <Icon
                :name="item.icon"
                size="20"
                class="mr-2"
              />
              <span class="font-medium">{{ t(item.name) }}</span>
            </NuxtLink>
          </li>

          <!-- Clear Cache Button -->
          <li>
            <Button
              :disabled="isClearingCache"
              class="w-full justify-start p-3 text-left"
              severity="info"
              @click="handleClearCache"
            >
              <Icon
                :name="isClearingCache ? 'ph:spinner' : 'ph:arrow-clockwise'"
                size="20"
                class="mr-2"
                :class="{ 'animate-spin': isClearingCache }"
              />
              <span class="font-medium">
                {{ isClearingCache ? t('Clearing...') : t('Clear Cache') }}
              </span>
            </Button>
          </li>

          <!-- Logout item -->
          <li
            v-for="item in navigation.filter(item => item.action === 'logout')"
            :key="item.name"
          >
            <a
              class="flex items-center rounded border p-3 no-underline transition duration-150 hover:bg-gray-100 cursor-pointer"
              @click="handleItemClick(item)"
            >
              <Icon
                :name="item.icon"
                size="20"
                class="mr-2"
              />
              <span class="font-medium">{{ t(item.name) }}</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </Drawer>
</template>
